/**
 * Server-authoritative game engine contract.
 *
 * Every game is a pure state machine: the server holds the full state
 * (including hidden cards and the RNG seed), validates each move here,
 * and sends each player only `view(state, seat)` — never the raw state.
 */

export type Seat = number;
export type BotLevel = "easy" | "medium" | "hard";

export interface MoveResult<S> {
  ok: true;
  state: S;
  events: GameEvent[];
}
export interface MoveError {
  ok: false;
  error: string;
}

export interface GameEvent {
  type: string;
  seat?: Seat;
  [k: string]: unknown;
}

export interface GameResult {
  /** Seats ranked from first place. Ties share an inner array. */
  ranking: Seat[][];
  /** Points per seat for this match (game-specific meaning). */
  scores: number[];
  reason: string;
}

export interface GameEngine<S = any, M = any, V = any, O = any> {
  id: string;
  minPlayers: number;
  maxPlayers: number;
  /** Allowed player counts (subset of min..max). */
  playerCounts: number[];
  defaultOptions: O;
  init(playerCount: number, seed: number, options?: Partial<O>): S;
  /** Seat whose action is awaited, or null when the game is over. */
  currentSeat(state: S): Seat | null;
  applyMove(state: S, seat: Seat, move: M): MoveResult<S> | MoveError;
  /** What `seat` may see. seat = -1 for spectators. */
  view(state: S, seat: Seat): V;
  result(state: S): GameResult | null;
  /** Move to play automatically when a seat times out or disconnects. */
  autoMove(state: S, seat: Seat): M | null;
  /**
   * Computer opponent. Sees only what `seat` may see (never hidden cards or the RNG).
   * Optional: games without a dedicated bot fall back to `autoMove`.
   */
  botMove?(state: S, seat: Seat, level: BotLevel): M | null;
}

export const err = (error: string): MoveError => ({ ok: false, error });

/** Deep clone for immutable updates of plain JSON state. */
export function clone<T>(v: T): T {
  return structuredClone(v);
}
